import { onDocumentLoaded } from "../utils/on-document-loaded.js";
import { __SITE_CONSTANTS__ } from "../constants.js";
import { formatWeight, formatPrice, formattedDateTime } from "../lib/formatters.js";
import { transformLocale } from "../lib/transformLocal.js";

onDocumentLoaded(() => {
    const queryParams = Object.fromEntries(new URLSearchParams(location.search));
    let isDev = "dev" in queryParams && queryParams.dev === '1';

    const getReceiptTemplate = ({ qrCodeBase64, ...receipt }) => {
        const delimiter = `<hr style="border-style: dashed; border-color: rgba(0,0,0,0.3);"/>`;

        const totalCostWithDiscount = receipt.totalCost - (receipt.payments?.DISCOUNT || 0);

        const getTemplateRow = (props) => {
            const {
                isStrong,
                isTitle,
                label,
                value
            } = props;

            if (isTitle) {
                return `
                    <div class="transit-receipt__title-row">
                        ${isStrong ? `<strong>${label}</strong>` : `<span>${label}</span>`}
                        ${isStrong ? `<strong>${value}</strong>` : `<span>${value}</span>`}
                    </div>
                `;
            }

            return `
                <div class="transit-receipt__row">
                    ${isStrong ? `<strong>${label}:</strong>` : `<span>${label}:</span>`}
                    <hr style="border-style: dotted; flex: 1; margin: 0 6px;"/>
                    ${isStrong ? `<strong>${value}</strong>` : `<span>${value}</span>`}
                </div>
            `;
        };

        const products = receipt.receiptDetails?.map((product, index) => {
            return {
                isTitle: true,
                label: `${index + 1}.`,
                value: product.productName,
                sub: [
                    {
                        label: `${formatWeight(product.qty, true, false)} ${product.unitName} x ${formatPrice(product.price, true)}`,
                        value: formatPrice(product.amount, true)
                    },
                    {
                        label: "Скидка",
                        value: product.discount ? formatPrice(product.discount, true) : "0"
                    },
                    {
                        label: "Штрихкод",
                        value: !!product.barcode && product.barcode
                    },
                    {
                        label: "ИКПУ",
                        value: product.vatBarcode
                    },
                    {
                        label: "Акциз",
                        value: !!product.excise && formatPrice(product.excise, true)
                    },
                    {
                        label: `в т.ч. НДС - ${product.ndsPercent || 0}%`,
                        value: formatPrice(product.nds, true)
                    },
                    {
                        label: "Кол-во маркировок",
                        value: !!product.marks && product.marks?.length
                    },
                    {
                        label: "ИНН Комитента",
                        value: !!product.committentTin && product.committentTin
                    }
                ].map((item) => !!item.value ? getTemplateRow({
                    isTitle: false,
                    isStrong: false,
                    label: item.label,
                    value: item.value
                }) : null).join("")
            }
        });

        const options = [
            {
                label: "ИНН",
                value: receipt.companyINN
            },
            {
                label: "Номер ККМ",
                value: receipt.terminalSN
            },
            {
                value: delimiter
            },
            {
                label: "Кассир",
                value: `${receipt.user?.fullName?.lastName} ${receipt.user?.fullName?.firstName}`
            },
            {
                label: "Смена",
                value: receipt.shiftNo
            },
            {
                label: "Чек в смене",
                value: receipt.additional?.numberInShift,
                sub: [
                    {
                        label: "Дата и время",
                        value: formattedDateTime(receipt.receiptDateTime)
                    },
                ].map((item) => !!item.value ? getTemplateRow({
                    isTitle: false,
                    isStrong: false,
                    label: item.label,
                    value: item.value
                }) : null).join("")
            },
            {
                label: "Тип чека",
                value: receipt.status?.nameRu
            },
            {
                value: delimiter
            },
            ...products,
            {
                value: delimiter
            },
            {
                label: "Позиций",
                value: receipt.receiptDetails?.length
            },
            {
                label: "Итого",
                value: formatPrice(receipt.totalCost, true)
            },
            {
                label: "Скидка",
                value: receipt.payments?.DISCOUNT ? formatPrice(receipt.payments?.DISCOUNT, true) : "0"
            },
            {
                isStrong: true,
                label: "К оплате",
                value: formatPrice(totalCostWithDiscount, true)
            },
            {
                isStrong: true,
                label: "Оплачено",
                value: formatPrice(receipt.totalPaid || 0, true),
                sub: transformLocale().map((item) => {
                    const sum = receipt.payments[item.type];

                    if (!sum) return null;

                    return sum ? getTemplateRow({
                        isTitle: false,
                        isStrong: false,
                        label: item.name,
                        value: formatPrice(sum, true)
                    }) : null;
                }).join("")
            },
            {
                isStrong: true,
                label: "НДС",
                value: receipt.totalNds && formatPrice(receipt.totalNds, true) || receipt.payments.NDS && formatPrice(receipt.payments.NDS, true)
            },
            {
                value: delimiter
            },
            {
                label: "Номер чека",
                value: receipt.additional?.receiptSeq
            },
            {
                label: "UID",
                value: receipt.uid
            },
            {
                label: "ФМ",
                value: receipt.additional?.samModuleSerialNumber
            },
            {
                label: "ФП",
                value: receipt.fiscalSign
            },
        ];

        const rows = options.map((op) => {
            if (!op.label && op.value) {
                return op.value;
            }

            if (op.value || op.sub) {
                const row = getTemplateRow({
                    isTitle: op.isTitle,
                    isStrong: op.isStrong,
                    label: op.label,
                    value: op.value
                });
                const sub = op.sub ? `
                            <div class="transit-receipt__sub">
                                ${op.sub}
                            </div>` : "";

                if (op.value) {
                    return row + sub;
                }

                return sub;
            }
        });

        return `
            <div class="transit-receipt">
                <div class="transit-receipt__name">
                    <strong>${receipt.companyName}</strong>
                    <span>${receipt.branchName}</span>
                    <span>${receipt.branchAddress}</span>
                </div>
                ${rows.join("")}
                <div class="transit-receipt__qr-code">
                    <img src="${qrCodeBase64}" alt="receipt-qr-code"/>
                </div>
            </div>
        `;
    };

    const getReceipt = async (uid, qrCodeBase64) => {
        const receiptModal = document.querySelector('[data-current-modal="transitReceiptModal"] .site-modal__content');

        try {
            receiptModal.classList.add("loading");
            const response = await fetch(`${isDev ? __SITE_CONSTANTS__.devBaseUrl : __SITE_CONSTANTS__.prodBaseUrl}/api/public/receipt/${uid}`);
            const data = await response.json();

            if (response.ok) {
                const receiptTemplate = getReceiptTemplate({
                    ...data,
                    qrCodeBase64
                });

                receiptModal.innerHTML = receiptTemplate;
            } else {
                throw new Error(data?.detail);
            }
        } catch (e) {
            const message = e.message;
            console.log(message);
        } finally {
            receiptModal.classList.remove("loading");
        }
    };

    const handleClick = (e) => {
        const target = e.target;

        if (target.closest(".transit-page__receipt-btn")) {
            let button = target;

            if (target.tagName !== "BUTTON") {
                button = target.closest("button");
            }

            let uid = button.dataset.receiptUid;
            let qrCodeBase64 = target.closest("tr").querySelector(".transit-page__qr-code img").getAttribute("src");

            getReceipt(uid, qrCodeBase64);
        }
    };

    document.addEventListener("click", handleClick);
});